import type { SymbolSupportDTO, SymbolCompatibilityDTO } from '@/services/forexApi';
export type AccountStatus = 'reviewing' | 'provisioning' | 'failed' | 'status_unknown' | 'connected' | 'released';

export const accountStatusConfig: Record<AccountStatus, {
  label: string;
  color: string;
  bg: string;
  dot: string;
  selectable: boolean;
  description: string;
}> = {
  reviewing: {
    label: '接入中',
    color: '#F59E0B',
    bg: 'bg-amber-50',
    dot: 'bg-amber-400',
    selectable: false,
    description: '平台正在验证账号并准备跟单环境',
  },
  failed: { label: '开通失败', color: '#EF4444', bg: 'bg-red-50', dot: 'bg-red-400', selectable: false, description: 'EA 开通失败，请查看失败原因和重新提交条件' },
  status_unknown: { label: '状态待确认', color: '#6B7280', bg: 'bg-gray-50', dot: 'bg-gray-400', selectable: false, description: '开通状态暂不可确认，请刷新或联系管理员' },
  provisioning: {
    label: 'EA 开通中',
    color: '#6366F1',
    bg: 'bg-indigo-50',
    dot: 'bg-indigo-400',
    selectable: false,
    description: '平台正在自动准备 EA，连接验证完成后才能跟随',
  },
  connected: {
    label: '已接入',
    color: '#10B981',
    bg: 'bg-green-50',
    dot: 'bg-green-400',
    selectable: true,
    description: '接入已完成，跟随资格按所选策略核对',
  },
  released: {
    label: '已释放',
    color: '#9CA3AF',
    bg: 'bg-gray-100',
    dot: 'bg-gray-400',
    selectable: false,
    description: '可重新绑定',
  },
};

export type FollowStatus =
  | 'not_started'
  | 'configuring'
  | 'pending_risk_confirm'
  | 'running'
  | 'paused_by_user'
  | 'paused_by_platform'
  | 'agent_unavailable'
  | 'status_unknown'
  | 'stopped';

export const followStatusConfig: Record<FollowStatus, { label: string; color: string; bg: string; text: string }> = {
  not_started: { label: '未开启', color: '#9CA3AF', bg: 'bg-gray-50', text: 'text-gray-500' },
  configuring: { label: '配置中', color: '#8B5CF6', bg: 'bg-purple-50', text: 'text-purple-600' },
  pending_risk_confirm: { label: '待确认', color: '#F59E0B', bg: 'bg-amber-50', text: 'text-amber-600' },
  running: { label: '关系运行中', color: '#10B981', bg: 'bg-green-50', text: 'text-green-600' },
  paused_by_user: { label: '已暂停', color: '#F59E0B', bg: 'bg-amber-50', text: 'text-amber-600' },
  paused_by_platform: { label: '平台暂停', color: '#F97316', bg: 'bg-orange-50', text: 'text-orange-600' },
  agent_unavailable: { label: '不可用', color: '#EF4444', bg: 'bg-red-50', text: 'text-red-600' },
  status_unknown: { label: '状态待核对', color: '#6B7280', bg: 'bg-gray-50', text: 'text-gray-500' },
  stopped: { label: '已停止', color: '#6B7280', bg: 'bg-gray-100', text: 'text-gray-500' },
};

export interface BrokerAccount {
  symbolSupport?: SymbolSupportDTO;
  symbolCompatibility?: SymbolCompatibilityDTO;
  accountEnvironment?: 'simulation' | 'real' | 'unknown';
  exitOnly?: boolean;
  executionReadiness?: {
    state: 'ready' | 'not_ready' | 'exit_only' | 'unknown';
    effective_for_new_opens: boolean;
    reason_codes: string[];
    observed_at: number | null;
  };
  onboarding?: { stage: string; stage_label: string; submission_revision?: number };
  usageStatus?: 'available' | 'following' | 'occupied';
  id: string;
  type: 'mt4' | 'mt5' | 'api';
  brokerName: string;
  accountNo: string;
  server: string;
  balance: string;
  margin: string;
  accountStatus: AccountStatus;
  connectedAt: string;
  login?: string;
  password?: string;
  apiKey?: string;
  apiSecret?: string;
  apiEndpoint?: string;
  testMode?: boolean;
}

export interface FollowRecord {
  id: string;
  agentId: number | string;
  accountId: string;
  accountNo: string;
  server: string;
  brokerName: string;
  followStatus: FollowStatus;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface FollowConfig {
  allowedPairs: string[];
}

export const defaultFollowConfig: FollowConfig = {
  allowedPairs: [],
};
